import { useCallback, useMemo, useRef, useState } from "react";
import { bandCounts, rankOf, sortedGuesses, type Guess } from "../../worker/games/contexto/game";
import { api, ApiFailure } from "../api";
import { ContextoRow } from "../components/ContextoRow";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface View {
  guesses: Guess[];
  last: Guess | null;
  tips: number;
  best: number | null;
  bands: { green: number; yellow: number; red: number };
  answer?: string;
}

const COPY = {
  unknown_word: "Не знаю такого слова",
  bad_request: "Одне слово, літерами",
  upstream: "contexto.me не відповідає. Спробуй за хвилину.",
};

export function Contexto({ date }: { date?: string | undefined }) {
  const g = useGame<View>("contexto", COPY, date);
  const [word, setWord] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const sorted = useMemo(() => (state ? sortedGuesses(state.view.guesses) : []), [state?.view.guesses]);

  const submit = useCallback(async () => {
    const w = word.trim();
    if (!w || !state || finished || g.busy) return;
    inputRef.current?.focus();
    try {
      const next = await api.guess<View>("contexto", w, date);
      g.apply(next);
      setWord("");
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "server_error";
      if (code === "already_guessed") {
        const gg = (e instanceof ApiFailure ? (e.extra as { guess?: Guess } | undefined)?.guess : undefined) ?? undefined;
        g.showToast(gg ? `Вже було: ${gg.w} — ${rankOf(gg.d)}` : "Вже було");
      } else if (code === "game_over") void g.load();
      else g.showToast(COPY[code as keyof typeof COPY] ?? `Щось зламалось (${code})`, 2000);
    }
  }, [word, state, finished, g]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const last = v.last;
  const won = s.status === "won";
  const bands = bandCounts(v.guesses);

  return (
    <Shell header={<Header left={`#${s.number}`} title="CONTEXTO" right={`🔥 ${s.me.streak}`} />}>
      <div className="px-4 pb-1 text-xs text-tghint">
        спроб: <b className="text-tgtext">{v.guesses.length}</b>
        {v.tips > 0 ? <> · підказок: <b className="text-tgtext">{v.tips}</b></> : null}
        {finished && v.answer ? <> · слово: <b className="text-tgtext">{v.answer}</b></> : null}
      </div>
      <form
        className="flex gap-2 px-4 pb-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <input
          ref={inputRef}
          value={word}
          onChange={(e) => setWord(e.target.value)}
          readOnly={finished}
          placeholder={finished ? "гра закінчена" : "введи слово"}
          autoComplete="off"
          autoCorrect="on"
          autoCapitalize="none"
          spellCheck
          enterKeyHint="send"
          autoFocus
          className="h-[46px] flex-1 rounded-xl border border-[rgba(128,128,128,0.35)] bg-transparent px-4 text-[16px] text-tgtext outline-none placeholder:text-tghint/60 focus:border-tgbutton disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={finished || g.busy || !word.trim()}
          onMouseDown={(e) => e.preventDefault()}
          className="h-[46px] rounded-xl bg-tgbutton px-5 text-[15px] font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
        >
          {g.busy ? "…" : "→"}
        </button>
      </form>

      <div className="flex-1 overflow-y-auto px-4 pb-3 pt-1" onTouchMove={() => inputRef.current?.blur()}>
        {last && !finished && (
          <div className="mb-3">
            <ContextoRow guess={last} pinned />
          </div>
        )}
        {sorted.length === 0 ? (
          <div className="mt-10 text-center text-sm text-tghint">Вгадай секретне слово. Число = наскільки близько по змісту. 1 — це воно.</div>
        ) : (
          <div className="space-y-[6px]">
            {sorted.map((x) => (
              <ContextoRow key={x.w} guess={x} />
            ))}
          </div>
        )}
      </div>

      {!finished && (
        <div className="flex gap-2 px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pt-1">
          <button
            type="button"
            disabled={g.busy}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => void g.run(() => api.guess<View>("contexto", "?", date))}
            className="flex-1 rounded-xl bg-[rgba(128,128,128,0.18)] py-3 text-sm font-semibold active:opacity-70 disabled:opacity-40"
          >
            💡 підказка
          </button>
          <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("contexto", date))} />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Є слово" : "Здався"}
          subtitle={
            won
              ? `${v.guesses.length} спроб${v.tips ? ` · ${v.tips} підказ.` : ""} · стрік ${s.me.streak} 🔥`
              : `слово було «${v.answer ?? "?"}» · стрік згорів`
          }
          scoreLabel="avg спроб"
          formatScore={(n) => `${n} спроб`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          <div className="mt-3 text-center text-base">🟩 {bands.green} &nbsp; 🟨 {bands.yellow} &nbsp; 🟥 {bands.red}</div>
        </ResultSheet>
      )}
    </Shell>
  );
}
